/**
 * SEO Component - Dynamic meta tags for better SEO
 *
 * Uses react-helmet-async to update meta tags dynamically.
 * Note: For full SEO with bots, pre-rendering is still needed (Phase 3).
 *
 * Usage:
 *   <SEO
 *     title="Event Name"
 *     description="Event description"
 *     image="https://..."
 *     url="/event/123"
 *   />
 */

import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'event';
  // Event-specific props
  event?: {
    name: string;
    startDate: string;
    endDate?: string;
    location: string;
    description: string;
    image?: string;
    organizer?: string;
    price?: string;
    currency?: string;
  };
}

const BASE_URL = 'https://what2do.today';
const DEFAULT_IMAGE = `${BASE_URL}/images/og-image.png`;
const DEFAULT_TITLE = 'What 2 Do ? - Discover Local Events in Sri Lanka';
const DEFAULT_DESCRIPTION = 'Find the best events, concerts, festivals and activities near you in Sri Lanka. Connect with local organizers and never miss what\'s happening!';

export const SEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url = '',
  type = 'website',
  event
}: SEOProps) => {
  const fullTitle = title ? `${title} | What 2 Do ?` : DEFAULT_TITLE;
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  const fullImage = image.startsWith('http') ? image : `${BASE_URL}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Event Schema.org JSON-LD */}
      {event && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: event.name,
            startDate: event.startDate,
            endDate: event.endDate || event.startDate,
            location: {
              '@type': 'Place',
              name: event.location,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'LK',
                addressLocality: event.location
              }
            },
            description: event.description,
            image: event.image || fullImage,
            organizer: event.organizer ? {
              '@type': 'Organization',
              name: event.organizer
            } : undefined,
            offers: event.price ? {
              '@type': 'Offer',
              price: event.price,
              priceCurrency: event.currency || 'LKR',
              availability: 'https://schema.org/InStock'
            } : {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'LKR'
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
